import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FiPlay, FiInfo, FiPlus, FiCheck, FiStar, FiYoutube } from 'react-icons/fi';
import { IMG_BASE } from '../api/tmdb';
import { useMovieContext } from '../context/MovieContext';
import TrailerModal from './TrailerModal';

const Hero = ({ movies = [] }) => {
  const [index,    setIndex]    = useState(0);
  const [direction,setDirection]= useState(1);
  const [ytKey,    setYtKey]    = useState(null);
  const navigate = useNavigate();
  const { addToWatchlist, removeFromWatchlist, isInWatchlist } = useMovieContext();
  const { scrollY } = useScroll();
  const y       = useTransform(scrollY, [0, 600], [0, 160]);
  const opacity = useTransform(scrollY, [0, 380], [1, 0]);
  const scale   = useTransform(scrollY, [0, 380], [1, 1.07]);

  const next = useCallback(() => {
    setDirection(1);
    setIndex(i => (i + 1) % Math.min(movies.length, 10));
  }, [movies.length]);

  useEffect(() => {
    if (!movies.length) return;
    const t = setInterval(next, 7000);
    return () => clearInterval(t);
  }, [movies.length, next]);

  if (!movies.length) return (
    <div className="w-full h-screen flex items-center justify-center" style={{ background: 'var(--dark)' }}>
      <div className="w-14 h-14 rounded-full border-2 border-t-transparent animate-spin"
        style={{ borderColor: 'var(--red)', borderTopColor: 'transparent' }} />
    </div>
  );

  const movie     = movies[index];
  const isCustom  = movie?.source === 'custom';
  const inList    = isInWatchlist(movie?.id);

  // resolve images for both TMDB and custom movies
  const backdrop  = isCustom
    ? movie.backdropUrl
    : (movie.backdrop_path ? IMG_BASE + '/original' + movie.backdrop_path : null);
  const poster    = isCustom
    ? movie.posterUrl
    : (movie.poster_path ? IMG_BASE + '/w185' + movie.poster_path : null);
  const title     = movie?.title || '';
  const overview  = isCustom ? movie.description : movie.overview;
  const rating    = isCustom ? movie.rating : movie.vote_average;
  const year      = isCustom
    ? movie.releaseYear
    : movie.release_date?.split('-')[0];

  const variants = {
    enter:  d => ({ x: d > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit:   d => ({ x: d > 0 ? '-100%' : '100%', opacity: 0 }),
  };

  const handlePlay = () => {
    if (isCustom && movie.youtubeId) { setYtKey(movie.youtubeId); return; }
    navigate('/movie/' + movie.id);
  };

  const handleInfo = () => {
    if (isCustom) { navigate('/movie/custom_' + (movie._customId || movie.id)); return; }
    navigate('/movie/' + movie.id);
  };

  return (
    <div className="relative w-full overflow-hidden" style={{ height: '100vh', minHeight: 600 }}>
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        {movies.slice(0, 10).map((m, i) => {
          const bg = m.source === 'custom'
            ? m.backdropUrl
            : (m.backdrop_path ? IMG_BASE + '/original' + m.backdrop_path : null);
          return (
            <motion.div key={m.id + '_' + i} custom={direction} variants={variants}
              initial="enter" animate={i === index ? 'center' : 'exit'}
              transition={{ duration: 0.75, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="absolute inset-0">
              {bg
                ? <img src={bg} alt={m.title} className="w-full h-full object-cover"
                    style={{ filter: 'brightness(0.42)' }} />
                : <div className="w-full h-full" style={{ background: '#0d0d1a' }} />
              }
            </motion.div>
          );
        })}
      </motion.div>

      {/* Gradient overlays */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(to right, rgba(10,10,15,0.96) 0%, rgba(10,10,15,0.5) 50%, transparent 100%)',
      }} />
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(to top, rgba(10,10,15,1) 0%, rgba(10,10,15,0.15) 30%, transparent 60%)',
      }} />
      <div className="absolute bottom-0 left-0 right-0 h-0.5"
        style={{ background: 'linear-gradient(to right, var(--red), transparent 60%)' }} />

      {/* Content */}
      <motion.div style={{ opacity }} className="relative z-10 flex flex-col justify-end h-full pb-20 md:pb-32 px-6 md:px-16 lg:px-24 max-w-screen-xl">
        <motion.div key={movie.id} initial={{ y: 36, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}>

          {/* Badges row */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            {isCustom ? (
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-accent font-bold uppercase tracking-wider"
                style={{ background: 'rgba(245,197,24,0.9)', color: '#000' }}>
                <FiYoutube size={11} /> CineSpur Original
              </span>
            ) : (
              <span className="px-3 py-1 rounded-full text-xs font-accent font-bold uppercase tracking-wider"
                style={{ background: 'var(--red)', color: '#fff' }}>
                Featured
              </span>
            )}
            {rating > 0 && (
              <div className="flex items-center gap-1" style={{ color: 'var(--gold)' }}>
                <FiStar size={13} fill="currentColor" />
                <span className="text-sm font-accent">{parseFloat(rating).toFixed(1)}</span>
              </div>
            )}
            {year && <span className="text-xs" style={{ color: 'var(--muted)' }}>{year}</span>}
            {isCustom && movie.genre && (
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)', color: 'var(--muted)' }}>
                {movie.genre.split(',')[0].trim()}
              </span>
            )}
          </div>

          {/* Title */}
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-none mb-4 max-w-3xl"
            style={{ color: 'var(--text)', textShadow: '0 0 60px rgba(229,9,20,0.2)' }}>
            {title}
          </h1>

          {/* Overview */}
          {overview && (
            <p className="font-body text-sm md:text-base max-w-lg mb-8 leading-relaxed line-clamp-3"
              style={{ color: 'rgba(232,232,240,0.72)' }}>
              {overview}
            </p>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-4 flex-wrap">
            <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              onClick={handlePlay}
              className="flex items-center gap-2.5 px-8 py-3.5 rounded-full font-body font-semibold text-sm"
              style={{ background: 'var(--red)', color: '#fff', boxShadow: '0 0 30px rgba(229,9,20,0.45)' }}>
              {isCustom ? <><FiYoutube size={16} /> Watch Now</> : <><FiPlay size={15} fill="currentColor" /> Watch Now</>}
            </motion.button>

            <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              onClick={handleInfo}
              className="flex items-center gap-2.5 px-8 py-3.5 rounded-full font-body font-semibold text-sm glass hover:bg-white/10 transition-colors"
              style={{ color: 'var(--text)' }}>
              <FiInfo size={15} /> More Info
            </motion.button>

            {!isCustom && (
              <motion.button whileHover={{ scale: 1.1, rotate: inList ? 0 : 90 }} whileTap={{ scale: 0.9 }}
                onClick={() => inList ? removeFromWatchlist(movie.id) : addToWatchlist(movie)}
                className="w-12 h-12 rounded-full flex items-center justify-center glass hover:bg-white/10 transition-colors"
                style={{ color: inList ? 'var(--gold)' : 'var(--text)' }}>
                {inList ? <FiCheck size={18} /> : <FiPlus size={18} />}
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* Slide indicators */}
        <div className="flex items-center gap-2 mt-10">
          {movies.slice(0, 10).map((_, i) => (
            <button key={i}
              onClick={() => { setDirection(i > index ? 1 : -1); setIndex(i); }}
              className="rounded-full transition-all duration-300"
              style={{ width: i === index ? 24 : 7, height: 4,
                background: i === index ? 'var(--red)' : 'rgba(255,255,255,0.2)' }} />
          ))}
        </div>
      </motion.div>

      {/* Right thumbnail strip */}
      <div className="absolute right-6 md:right-10 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-3 z-10">
        {movies.slice(0, 5).map((m, i) => {
          const thumb = m.source === 'custom'
            ? m.posterUrl
            : (m.poster_path ? IMG_BASE + '/w92' + m.poster_path : null);
          return (
            <motion.div key={m.id + '_t_' + i}
              whileHover={{ scale: 1.1, x: -5 }}
              onClick={() => { setDirection(i > index ? 1 : -1); setIndex(i); }}
              className="cursor-pointer rounded-xl overflow-hidden transition-all duration-200 flex items-center justify-center"
              style={{
                width: 58, height: 82,
                background: '#1a1a2e',
                outline: i === index ? '2px solid var(--red)' : '1px solid rgba(255,255,255,0.08)',
                outlineOffset: i === index ? 2 : 0,
                opacity: i === index ? 1 : 0.48,
              }}>
              {thumb
                ? <img src={thumb} alt={m.title} className="w-full h-full object-cover" />
                : <FiYoutube size={18} style={{ color: 'var(--muted)' }} />
              }
            </motion.div>
          );
        })}
      </div>

      {/* YouTube modal for custom hero movies */}
      <TrailerModal videoKey={ytKey} onClose={() => setYtKey(null)} />
    </div>
  );
};

export default Hero;
